/**
 * A one-line offer, beside the work it applies to.
 *
 * Settings → Integrations is where the assistant is explained, and almost
 * nobody goes to Settings. The person who would most want this is looking at an
 * empty estimates list at the time, thinking about the quote they have to write
 * — so the offer belongs there, in the moment, rather than two navigations away
 * behind a word ("Integrations") that does not describe what it does.
 *
 * Dismissible, and dismissed for good. A hint that reappears after it has been
 * refused stops being a hint; `localStorage` is exactly right for this because
 * the cost of losing it — on a new browser, in a private window — is that
 * somebody sees a single line of text again. Every access is guarded, because a
 * browser with site data blocked throws on the property itself rather than
 * returning nothing.
 */
import { useState } from "react";

const KEY = "dantria.assistant-hint.dismissed";

function alreadyDismissed(): boolean {
  try {
    return window.localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

function remember() {
  try {
    window.localStorage.setItem(KEY, "1");
  } catch {
    /* A hint that cannot remember being dismissed is still only a hint. */
  }
}

export function AssistantHint({ what }: { what: string }) {
  const [gone, setGone] = useState(alreadyDismissed);
  if (gone) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm shadow-sm">
      <p className="min-w-0 flex-1 text-neutral-600">
        <span className="font-medium text-neutral-900">Dictate this instead.</span> {what} Connect
        Claude or ChatGPT and it prices off your own rates.
      </p>
      <a
        className="font-medium text-neutral-900 underline underline-offset-2"
        href="/assistant"
        target="_blank"
        rel="noreferrer"
      >
        See what to say
      </a>
      <button
        type="button"
        className="text-neutral-400 hover:text-neutral-600"
        aria-label="Dismiss"
        onClick={() => {
          remember();
          setGone(true);
        }}
      >
        Dismiss
      </button>
    </div>
  );
}
